/**
 * MCQ option shuffle for practice sessions.
 * "All of the above" / "Any of the above" / "None of the above" style choices stay on the last letter (D for 4 options).
 */

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

const EN_ANCHOR_RES: RegExp[] = [
  /\ball of the above\b/i,
  /\bany of the above\b/i,
  /\bany one of the above\b/i,
  /\bnone of the above\b/i,
  /\bboth of the above\b/i,
  /\ball of these\b/i,
  /\bany of these\b/i,
  /\bnone of these\b/i,
  /\bboth a and b\b/i,
  /\bboth a and b are correct\b/i,
  /\ba and b are correct\b/i,
  /\ball answers are correct\b/i,
  /\ball are correct\b/i,
];

const FR_ANCHOR_SUBSTRINGS = [
  "toutes les reponses ci-dessus",
  "toutes les propositions ci-dessus",
  "toutes reponses ci-dessus",
  "aucune des reponses ci-dessus",
  "aucune des propositions ci-dessus",
  "aucune de ces reponses",
  "les deux reponses ci-dessus",
  "les deux propositions ci-dessus",
  "toutes les reponses precedentes",
  // "Any of the above" (at least one correct / pick-any style)
  "n'importe laquelle des reponses ci-dessus",
  "n'importe laquelle des propositions ci-dessus",
  "n\u2019importe laquelle des reponses ci-dessus",
  "n\u2019importe laquelle des propositions ci-dessus",
  "l'une ou l'autre des reponses ci-dessus",
  "l'une ou l'autre des propositions ci-dessus",
];

function textMatchesAnchor(raw: string): boolean {
  const t = raw.trim();
  if (t.length < 6) return false;
  if (EN_ANCHOR_RES.some((re) => re.test(t))) return true;
  const n = normalize(t);
  return FR_ANCHOR_SUBSTRINGS.some((frag) => n.includes(frag));
}

/** Index of the option that must stay last (bottom / D), or null if none. */
export function findAnchorBottomOptionIndex(
  options: string[],
  optionsFr: (string | null | undefined)[] | null | undefined,
): number | null {
  let lastMatch: number | null = null;
  for (let i = 0; i < options.length; i++) {
    const en = options[i] ?? "";
    if (textMatchesAnchor(en)) {
      lastMatch = i;
      continue;
    }
    const fr = optionsFr?.[i];
    if (typeof fr === "string" && fr.trim() && textMatchesAnchor(fr)) {
      lastMatch = i;
    }
  }
  return lastMatch;
}

function fisherYatesShuffle(arr: number[]): number[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    const j = buf[0]! % (i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * Returns a permutation of [0..optionCount-1]: shuffled, except any
 * "all/any/none/both of the above" option is always moved to the last slot.
 */
export function shuffleMcqOptionIndices(
  optionCount: number,
  options: string[],
  optionsFr: (string | null | undefined)[] | null | undefined,
): number[] {
  const base = Array.from({ length: optionCount }, (_, i) => i);
  if (optionCount <= 1) return base;

  const anchor = findAnchorBottomOptionIndex(options, optionsFr);
  if (anchor === null) {
    return fisherYatesShuffle(base);
  }

  const others = base.filter((i) => i !== anchor);
  const shuffledOthers = fisherYatesShuffle(others);
  return [...shuffledOthers, anchor];
}
